import { Component } from 'react';

import { ATC } from '@stratumn/atomic';

import { graphql } from '@apollo/client/react/hoc';
import { Download } from '@stratumn/icons';
import gql from 'graphql-tag';
import compose from 'lodash.flowright';
import Path from 'path-to-regexp';
import PropType from 'prop-types';

import { withError } from '@/components/errorBoundary/errorBoundary';
import { Navbar } from '@/components/layouts';
import { KanbanHeader, KanbanMain } from '@/components/layouts/kanban';
import { history } from '@/components/root';
import {
  ROUTE_WORKFLOW_DASHBOARD,
  ROUTE_WORKFLOW_EXPORT,
  ROUTE_WORKFLOW_OVERVIEW
} from '@/constant/routes';
import { FILTERS_QUERY } from '@/gql/localQueries';
import fragments from './fragments';
import GroupColumn from './group';

export class WorkflowBoard extends Component {
  static propTypes = {
    match: PropType.object.isRequired,
    filtersQuery: PropType.object,
    workflowQuery: PropType.object.isRequired,

    errorContext: PropType.object.isRequired
  };

  static defaultProps = {
    filtersQuery: {}
  };

  setDocTitle() {
    const { workflowQuery: { workflowByRowId: { name } = {} } = {} } =
      this.props;
    if (name) document.title = `${name} - Kanban - Trace`;
  }

  componentDidMount() {
    this.setDocTitle();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.workflowQuery !== this.props.workflowQuery)
      this.setDocTitle();
  }

  subscribeToWorkflow(props) {
    const { workflowQuery, match } = props;
    this.unsubscribeFromWorkflow = workflowQuery.subscribeToMore({
      document: subscriptions.workflow,
      variables: { id: `workflow:${match.params.id}` },
      updateQuery: (_prev, data) => ({
        workflowByRowId: data.subscriptionData.data.listen.relatedNode
      })
    });
  }

  UNSAFE_componentWillMount() {
    this.subscribeToWorkflow(this.props);
  }

  componentWillUnmount() {
    this.unsubscribeFromWorkflow();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      filtersQuery,
      workflowQuery: { loading, workflowByRowId, error },
      errorContext: { handleError },
      match: { params }
    } = nextProps;

    // This check avoids rerendering the component after the erroBoundary has been triggered
    if (!(loading || workflowByRowId)) {
      handleError('workflow', params.id, ROUTE_WORKFLOW_DASHBOARD, error);
      return;
    }

    if (filtersQuery.filters === this.props.filtersQuery.filters) {
      return;
    }

    if (this.unsubscribeFromWorkflow) {
      this.unsubscribeFromWorkflow();
    }

    this.subscribeToWorkflow(nextProps);
  }

  handleExport = () =>
    history.push(
      Path.compile(ROUTE_WORKFLOW_EXPORT)({
        id: this.props.match.params.id
      })
    );

  renderNavbar = () => {
    const {
      match,
      workflowQuery: { loading, workflowByRowId: workflow }
    } = this.props;

    const navbarConfig = {
      loading,
      bottomLevel: {
        workflowPage: true,
        infoContext: {
          links: [
            {
              icon: 'TableColumns',
              label: workflow?.name ?? 'Workflow'
            }
          ]
        },
        actions: {
          viewToggle: [
            {
              label: 'overview',
              path: Path.compile(ROUTE_WORKFLOW_OVERVIEW)({
                id: match.params.id
              }),
              active: false
            },
            {
              label: 'kanban',
              active: true
            }
          ],
          links: [
            {
              icon: <Download />,
              label: 'Export data',
              onClick: this.handleExport
            }
          ]
        }
      }
    };

    return <Navbar config={navbarConfig} />;
  };

  renderMain = () => {
    const {
      workflowQuery: { workflowByRowId: workflow }
    } = this.props;

    return (
      <ATC>
        {workflow.groups.nodes.map(group => (
          <GroupColumn group={group} key={group.rowId} />
        ))}
      </ATC>
    );
  };

  render = () => {
    const {
      workflowQuery: { loading, error, workflowByRowId }
    } = this.props;

    /**
     * If the workflow id doesn't exist,
     * we return null and let errorBoundary render the error page
     */
    if (error || !(workflowByRowId || loading)) {
      return null;
    }

    return (
      <>
        <KanbanHeader>{this.renderNavbar()}</KanbanHeader>
        <div />
        <KanbanMain loading={loading}>
          {loading ? null : this.renderMain()}
        </KanbanMain>
      </>
    );
  };
}

export const queries = {
  workflowQuery: gql`
    query workflowQuery($workflowId: BigInt!, $search: String) {
      workflowByRowId(rowId: $workflowId) {
        ...WorkflowQueryFragment
      }
    }
    ${fragments.workflow}
  `
};

export const subscriptions = {
  workflow: gql`
    subscription listenWorkflow($id: String!, $search: String) {
      listen(topic: $id) {
        relatedNodeId
        relatedNode {
          ...WorkflowQueryFragment
        }
      }
    }
    ${fragments.workflow}
  `
};

export default compose(
  graphql(FILTERS_QUERY, {
    name: 'filtersQuery'
  }),
  graphql(queries.workflowQuery, {
    name: 'workflowQuery',
    options: ({ match }) => ({
      variables: { workflowId: match.params.id }
    })
  }),
  withError
)(WorkflowBoard);
